/**
 * Application Constants for RepuCall
 */

import type { ReputationSource, PricingPlan, LookupSettings } from '@/types';

// ============================================================================
// Application Info
// ============================================================================

export const APP_NAME = import.meta.env.VITE_APP_NAME || 'RepuCall';
export const APP_VERSION = import.meta.env.VITE_APP_VERSION || '1.0.0';
export const APP_DESCRIPTION = 'Caller ID Reputation Monitoring';

// ============================================================================
// Storage Keys
// ============================================================================

export const STORAGE_KEYS = {
  AUTH_TOKEN: 'repucall_auth_token',
  AUTH_USER: 'repucall_auth_user',
  COMPANY_DATA: 'repucall_company_data',
  PHONE_DATA: 'repucall_phone_data',
  SESSION_ID: 'repucall_session_id',
  THEME: 'repucall_theme',
  SIDEBAR_COLLAPSED: 'repucall_sidebar_collapsed',
} as const;

// ============================================================================
// API Configuration
// ============================================================================

export const API_CONFIG = {
  BASE_URL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080/api/v1',
  TIMEOUT: parseInt(import.meta.env.VITE_API_TIMEOUT || '30000', 10),
} as const;

// ============================================================================
// Authentication
// ============================================================================

export const AUTH_CONFIG = {
  TOKEN_EXPIRY_BUFFER: 5 * 60 * 1000, // 5 minutes before expiry
  SESSION_DURATION: 24 * 60 * 60 * 1000, // 24 hours
  GOOGLE_CLIENT_ID: import.meta.env.VITE_GOOGLE_CLIENT_ID || '',
  WORKOS_CLIENT_ID: import.meta.env.VITE_WORKOS_CLIENT_ID || '',
  WORKOS_REDIRECT_URI: import.meta.env.VITE_WORKOS_REDIRECT_URI || 'http://localhost:3000/auth/callback',
} as const;

// ============================================================================
// Reputation Sources
// ============================================================================

export const REPUTATION_SOURCES: ReputationSource[] = [
  'Nomorobo',
  'RoboKiller',
  'IceHook',
  'TrueSpam',
  'YouMail',
  'Sensor',
  'Verizon',
  'SmartCall',
  'TMobile',
  'FTC',
];

export const REPUTATION_SOURCE_COLORS: Record<ReputationSource, string> = {
  Nomorobo: '#EF4444',
  RoboKiller: '#F97316',
  IceHook: '#8B5CF6',
  TrueSpam: '#EC4899',
  YouMail: '#14B8A6',
  Sensor: '#3B82F6',
  Verizon: '#EF4444',
  SmartCall: '#10B981',
  TMobile: '#E11D48',
  FTC: '#1D4ED8',
};

export const REPUTATION_SOURCE_INFO: Record<ReputationSource, { name: string; description: string }> = {
  Nomorobo: { name: 'Nomorobo', description: 'Robocall blocking service database' },
  RoboKiller: { name: 'RoboKiller', description: 'AI-powered spam call blocker' },
  IceHook: { name: 'IceHook', description: 'Carrier-level spam detection' },
  TrueSpam: { name: 'TrueSpam', description: 'Community-reported spam database' },
  YouMail: { name: 'YouMail', description: 'Visual voicemail and spam protection' },
  Sensor: { name: 'Sensor', description: 'Real-time call analytics platform' },
  Verizon: { name: 'Verizon', description: 'Verizon carrier spam filter' },
  SmartCall: { name: 'Smart Call', description: 'Samsung Smart Call service' },
  TMobile: { name: 'T-Mobile', description: 'T-Mobile Scam Shield database' },
  FTC: { name: 'FTC', description: 'Federal Trade Commission complaints' },
};

// ============================================================================
// Default Lookup Settings
// ============================================================================

export const DEFAULT_LOOKUP_SETTINGS: LookupSettings = {
  sources: {
    Nomorobo: true,
    RoboKiller: true,
    IceHook: true,
    TrueSpam: true,
    YouMail: true,
    Sensor: true,
    Verizon: true,
    SmartCall: false,
    TMobile: false,
    FTC: true,
  },
};

// ============================================================================
// Pagination
// ============================================================================

export const PAGINATION_CONFIG = {
  DEFAULT_PAGE_SIZE: 25,
  PAGE_SIZE_OPTIONS: [10, 25, 50, 100],
  MAX_PAGE_SIZE: 100,
} as const;

// ============================================================================
// Pricing Plans
// ============================================================================

export const PRICING_PLANS: PricingPlan[] = [
  {
    id: 'trial',
    tier: 'trial',
    name: 'Trial',
    credits: 20,
    price: 0,
    trialDays: 14,
    description: 'Free 14-day trial',
    features: [
      '20 checks included',
      'All core features',
      'Email notifications',
    ],
    priceId: import.meta.env.VITE_STRIPE_PRICE_TRIAL || 'price_trial_placeholder',
  },
  {
    id: 'starter',
    tier: 'starter',
    name: 'Starter',
    credits: 500,
    price: 49,
    overageRate: 0.12,
    description: 'For small teams getting started',
    features: [
      '500 checks per month',
      '$0.12 per additional check',
      'Basic reputation monitoring',
      'Email notifications',
      'CSV import/export',
    ],
    priceId: import.meta.env.VITE_STRIPE_PRICE_STARTER || 'price_starter_placeholder',
  },
  {
    id: 'growth',
    tier: 'growth',
    name: 'Growth',
    credits: 2000,
    price: 149,
    overageRate: 0.09,
    description: 'For growing businesses',
    features: [
      '2,000 checks per month',
      '$0.09 per additional check',
      'Advanced reputation monitoring',
      'API access',
      'Custom phone groups',
      'Bulk CSV upload',
    ],
    extras: [
      'Priority support',
    ],
    priceId: import.meta.env.VITE_STRIPE_PRICE_GROWTH || 'price_growth_placeholder',
    popular: true,
  },
  {
    id: 'pro',
    tier: 'pro',
    name: 'Pro',
    credits: 5000,
    price: 349,
    overageRate: 0.07,
    description: 'For high-volume operations',
    features: [
      '5,000 checks per month',
      '$0.07 per additional check',
      'All reputation sources',
      'Priority support',
      'Webhook integrations',
      'Advanced analytics',
    ],
    extras: [
      'Dedicated support manager',
    ],
    priceId: import.meta.env.VITE_STRIPE_PRICE_PRO || 'price_pro_placeholder',
  },
  {
    id: 'enterprise',
    tier: 'enterprise',
    name: 'Enterprise',
    credits: 15000,
    price: 799,
    overageRate: 0.05,
    description: 'For large-scale operations',
    features: [
      '15,000 checks per month',
      '$0.05 per additional check',
      'All Pro features',
      'SSO/SAML support',
      'SLA guarantee',
      'Custom integrations',
      'Dedicated account manager',
    ],
    priceId: import.meta.env.VITE_STRIPE_PRICE_ENTERPRISE || 'price_enterprise_placeholder',
  },
];

// ============================================================================
// Stripe Configuration
// ============================================================================

export const STRIPE_CONFIG = {
  PUBLISHABLE_KEY: import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || 'pk_test_placeholder',
} as const;

// ============================================================================
// US States
// ============================================================================

export const US_STATES: Record<string, string> = {
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
  DC: 'District of Columbia',
};

// ============================================================================
// Line Types
// ============================================================================

export const LINE_TYPE_LABELS: Record<string, string> = {
  fixed: 'Landline',
  mobile: 'Mobile',
  fixed_or_mobile: 'Fixed/Mobile',
  voip: 'VoIP',
  unknown: 'Unknown',
};

// ============================================================================
// Operating Company Types
// ============================================================================

export const OPERATING_COMPANY_TYPE_LABELS: Record<string, string> = {
  clec: 'CLEC (Competitive Local Exchange Carrier)',
  ilec: 'ILEC (Incumbent Local Exchange Carrier)',
  ipes: 'IPES (Internet Protocol Enabled Services)',
  wireless: 'Wireless Carrier',
  cable: 'Cable Provider',
  other: 'Other',
};

// ============================================================================
// Phone Number Groups (Default)
// ============================================================================

export const DEFAULT_PHONE_NUMBER_GROUPS = [
  'Pool 1',
  'Pool 2',
  'Pool 3',
  'Inbound Sales',
  'Outbound Sales',
  'Customer Service',
  'Collections',
  'Marketing',
];

// Alias for backwards compatibility
export const DEFAULT_CALL_GROUPS = DEFAULT_PHONE_NUMBER_GROUPS;

export const PHONE_NUMBER_GROUP_COLORS = [
  { id: 'blue', name: 'Blue', value: '#3B82F6' },
  { id: 'green', name: 'Green', value: '#10B981' },
  { id: 'purple', name: 'Purple', value: '#8B5CF6' },
  { id: 'orange', name: 'Orange', value: '#F97316' },
  { id: 'pink', name: 'Pink', value: '#EC4899' },
  { id: 'teal', name: 'Teal', value: '#14B8A6' },
  { id: 'indigo', name: 'Indigo', value: '#6366F1' },
  { id: 'amber', name: 'Amber', value: '#F59E0B' },
  { id: 'cyan', name: 'Cyan', value: '#06B6D4' },
  { id: 'red', name: 'Red', value: '#EF4444' },
];

// ============================================================================
// Notification Frequencies
// ============================================================================

export const NOTIFICATION_FREQUENCIES = [
  { value: 'hourly', label: 'Hourly' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
] as const;

// ============================================================================
// Date Ranges
// ============================================================================

export const DATE_RANGE_OPTIONS = [
  { value: '7d', label: 'Last 7 days', days: 7 },
  { value: '14d', label: 'Last 14 days', days: 14 },
  { value: '30d', label: 'Last 30 days', days: 30 },
  { value: '60d', label: 'Last 60 days', days: 60 },
  { value: '90d', label: 'Last 90 days', days: 90 },
] as const;

// ============================================================================
// User Roles
// ============================================================================

export const USER_ROLE_LABELS: Record<string, string> = {
  admin: 'Administrator',
  member: 'Member',
  viewer: 'Viewer',
};

export const USER_ROLE_DESCRIPTIONS: Record<string, string> = {
  admin: 'Can manage users, billing, settings, and view all phone data',
  member: 'Can view and interact with phone data, access settings',
  viewer: 'Read-only access to phone data',
};

// ============================================================================
// Feature Flags
// ============================================================================

export const FEATURE_FLAGS = {
  ENABLE_MOCK_DATA: import.meta.env.VITE_ENABLE_MOCK_DATA !== 'false',
  ENABLE_LOGGING: import.meta.env.VITE_ENABLE_LOGGING !== 'false',
} as const;

// ============================================================================
// Routes
// ============================================================================

export const ROUTES = {
  HOME: '/',
  LOGIN: '/login',
  SIGNUP: '/signup',
  AUTH_CALLBACK: '/auth/callback',
  SET_PASSWORD: '/set-password',
  DASHBOARD: '/dashboard',
  PHONE_NUMBERS: '/phone-numbers',
  PHONE_DETAIL: '/phone-numbers/:id',
  PHONE_NUMBER_GROUPS: '/phone-number-groups',
  SETTINGS: '/settings',
  SETTINGS_USERS: '/settings/users',
  SETTINGS_BILLING: '/settings/billing',
  SETTINGS_NOTIFICATIONS: '/settings/notifications',
  SETTINGS_LOOKUPS: '/settings/lookups',
  SETTINGS_INTEGRATIONS: '/settings/integrations',
  SETTINGS_SECURITY: '/settings/security',
  // Admin Routes
  ADMIN: '/admin',
  ADMIN_CUSTOMERS: '/admin/customers',
  ADMIN_CUSTOMER_DETAIL: '/admin/customers/:id',
  ADMIN_BILLING: '/admin/billing',
  ADMIN_PROMOTIONS: '/admin/promotions',
  ADMIN_ANALYTICS: '/admin/analytics',
  ADMIN_AUDIT_LOG: '/admin/audit-log',
} as const;

// ============================================================================
// Validation Rules
// ============================================================================

export const VALIDATION_RULES = {
  PASSWORD_MIN_LENGTH: 8,
  NAME_MIN_LENGTH: 2,
  NAME_MAX_LENGTH: 100,
  COMPANY_NAME_MIN_LENGTH: 2,
  COMPANY_NAME_MAX_LENGTH: 200,
  DESCRIPTION_MAX_LENGTH: 500,
  CSV_MAX_ROWS: 10000,
  CSV_MAX_FILE_SIZE: 5 * 1024 * 1024, // 5MB
} as const;

// ============================================================================
// Webhook Configuration
// ============================================================================

import type { WebhookEvent, WebhookEventInfo } from '@/types';

export const WEBHOOK_EVENTS: WebhookEventInfo[] = [
  {
    id: 'phone.added',
    name: 'Phone Number Added',
    description: 'Triggered when a new phone number is added to your account',
    category: 'phone',
  },
  {
    id: 'phone.removed',
    name: 'Phone Number Removed',
    description: 'Triggered when a phone number is removed from your account',
    category: 'phone',
  },
  {
    id: 'phone.updated',
    name: 'Phone Number Updated',
    description: 'Triggered when phone number details are updated',
    category: 'phone',
  },
  {
    id: 'check.completed',
    name: 'Reputation Check Completed',
    description: 'Triggered when a reputation check finishes successfully',
    category: 'check',
  },
  {
    id: 'check.failed',
    name: 'Reputation Check Failed',
    description: 'Triggered when a reputation check encounters an error',
    category: 'check',
  },
  {
    id: 'flag.detected',
    name: 'Flag Detected',
    description: 'Triggered when a phone number is flagged by any source',
    category: 'flag',
  },
  {
    id: 'flag.cleared',
    name: 'Flag Cleared',
    description: 'Triggered when a flag is removed from a phone number',
    category: 'flag',
  },
  {
    id: 'bulk.import.completed',
    name: 'Bulk Import Completed',
    description: 'Triggered when a CSV bulk import finishes processing',
    category: 'bulk',
  },
];

export const WEBHOOK_EVENT_CATEGORIES = {
  phone: { name: 'Phone Numbers', color: 'primary' },
  check: { name: 'Reputation Checks', color: 'info' },
  flag: { name: 'Flags & Alerts', color: 'warning' },
  bulk: { name: 'Bulk Operations', color: 'success' },
} as const;

export const getWebhookEventsByCategory = () => {
  const grouped: Record<string, WebhookEventInfo[]> = {};
  for (const event of WEBHOOK_EVENTS) {
    if (!grouped[event.category]) {
      grouped[event.category] = [];
    }
    grouped[event.category].push(event);
  }
  return grouped;
};
